import { FunctionComponent, useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import Text from "../../Text";
import Button from "../../Button";
import FormControl from "../../FormControl";
import SvgFragment from "../../../fragments/SvgFragment";
import { styles } from "../../../styles/themes/style";

interface IRenameFeedModalProps {
  feedName: string;
  onConfirm: (newName: string) => void;
  onCancel: () => void;
}

const RenameFeedModal: FunctionComponent<IRenameFeedModalProps> = ({
  feedName,
  onConfirm,
  onCancel,
}) => {
  const [newName, setNewName] = useState<string>(feedName);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    setNewName(feedName);
  }, [feedName]);

  const handleSubmit = useCallback(async () => {
    if (!newName.trim() || newName.trim() === feedName) {
      onCancel();
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(newName.trim());
      onCancel(); // Close modal after successful rename
    } finally {
      setIsSubmitting(false);
    }
  }, [newName, feedName, onConfirm, onCancel]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !isSubmitting) {
        handleSubmit();
      }
    },
    [handleSubmit, isSubmitting]
  );

  const canSubmit =
    newName.trim() && newName.trim() !== feedName && !isSubmitting;

  return (
    <Container>
      <Header>
        <Text $heading3>Rename feed</Text>
        <X onClick={() => onCancel()}>
          <SvgFragment type="close" />
        </X>
      </Header>

      <FormSection>
        <FormControl $orientation="column">
          <Text $label>Feed name</Text>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter feed name"
            autoFocus
          />
        </FormControl>
      </FormSection>
      <Actions>
        <Button
          $type="blue"
          $width="101px"
          onClickHandler={handleSubmit}
          disabled={!canSubmit}
          isLoading={isSubmitting}
        >
          Rename feed
        </Button>
      </Actions>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  min-width: 400px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Input = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 2px solid #dae0e8;
  border-radius: 4px;
  background: #ffffff;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${styles.colors.primary600};
  }

  &::placeholder {
    color: #999;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const X = styled.div`
  position: absolute;
  right: 20px;
  cursor: pointer;

  & .svg {
    height: 24px;
    width: 24px;
  }
`;

export default RenameFeedModal;
